import { useState, useEffect, useRef, useCallback } from 'react';
import { useSuggestionContainerRefContext } from 'components/header/components/searchbar/hooks/suggestions-container-ref';

interface UseInVisualViewport {
  threshold?: number;
  elementRef?: React.RefObject<HTMLDivElement>;
}

interface UseInVisualViewportReturn {
  isInViewport: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const useInVisualViewport = (options: UseInVisualViewport = {}): UseInVisualViewportReturn => {
  const { threshold = 0, elementRef } = options;
  const [isInViewport, setIsInViewport] = useState(false);
  const { ref: scrollableContainerRef } = useSuggestionContainerRefContext();

  const checkVisibility = useCallback(() => {
    const element = elementRef?.current ?? containerRef.current;
    if (!element || !window.visualViewport) {
      return;
    }
    const viewport = window.visualViewport;
    if (!viewport) return;

    const elementRect = element.getBoundingClientRect();

    const visibleHeight = Math.max(
      0,
      Math.min(elementRect.bottom, viewport.height) - Math.max(elementRect.top, 0),
    );

    const visibleWidth = Math.max(
      0,
      Math.min(elementRect.right, viewport.width) - Math.max(elementRect.left, 0),
    );

    const elementArea = elementRect.width * elementRect.height;
    const visibleArea = visibleWidth * visibleHeight;

    const visibilityRatio = elementArea > 0 ? visibleArea / elementArea : 0;
    const isVisible = visibilityRatio > threshold;

    setIsInViewport(isVisible);
  }, [threshold, elementRef]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!window.visualViewport) {
      return;
    }

    checkVisibility();

    const visualViewport = window.visualViewport;
    visualViewport.addEventListener('resize', checkVisibility);
    if (scrollableContainerRef !== null && 'current' in scrollableContainerRef) {
      const scrollableContainer = (scrollableContainerRef as React.MutableRefObject<HTMLDivElement>).current;

      scrollableContainer?.addEventListener('scroll', checkVisibility);
      scrollableContainer?.addEventListener('resize', checkVisibility);
    }

    return () => {
      visualViewport.removeEventListener('resize', checkVisibility);
      if (scrollableContainerRef !== null && 'current' in scrollableContainerRef) {
        const scrollableContainer = (scrollableContainerRef as React.MutableRefObject<HTMLDivElement>)
          .current;
        scrollableContainer?.removeEventListener('scroll', checkVisibility);
        scrollableContainer?.removeEventListener('resize', checkVisibility);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isInViewport, containerRef };
};
